import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { MessageCircle, Send, Clock, CheckCheck, User, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI } from '../../services/apiService';

const MessagesManagement = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isReplyDialogOpen, setIsReplyDialogOpen] = useState(false);
  const [replyText, setReplyText] = useState('');

  useEffect(() => {
    const isAdminAuth = localStorage.getItem('isAdminAuthenticated');
    if (!isAdminAuth) {
      navigate('/admin/login');
    } else {
      loadMessages();
    }
  }, [navigate]);

  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredMessages(messages);
    } else {
      setFilteredMessages(messages.filter(m => m.status.toLowerCase() === statusFilter));
    }
  }, [statusFilter, messages]);

  const loadMessages = async () => {
    try {
      const data = await adminAPI.getAllMessages();
      setMessages(data);
      setFilteredMessages(data);
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Failed to load messages');
    }
  };

  const handleOpenReply = (message) => {
    setSelectedMessage(message);
    setReplyText('');
    setIsReplyDialogOpen(true);
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    try {
      await adminAPI.replyToMessage(selectedMessage._id || selectedMessage.id, replyText);
      await loadMessages();
      setIsReplyDialogOpen(false);
      toast.success('Reply sent successfully!');
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    }
  };

  const handleCloseMessage = async (messageId) => {
    try {
      await adminAPI.updateMessageStatus(messageId, 'Read');
      await loadMessages();
      toast.success('Message marked as read');
    } catch (error) {
      console.error('Error updating message status:', error);
      toast.error('Failed to update message status');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      'Unread': { className: 'bg-orange-100 text-orange-800', icon: Clock },
      'Read': { className: 'bg-blue-100 text-blue-800', icon: MessageCircle },
      'Replied': { className: 'bg-green-100 text-green-800', icon: CheckCheck }
    };
    const { className, icon: Icon } = config[status] || config['Unread'];
    return (
      <Badge className={className}>
        <Icon className="h-3 w-3 mr-1" />
        {status}
      </Badge>
    );
  };

  const unreadCount = messages.filter(m => m.status === 'Unread').length;
  const readCount = messages.filter(m => m.status === 'Read').length;
  const repliedCount = messages.filter(m => m.status === 'Replied').length;

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8 bg-white/80 backdrop-blur-sm p-6 rounded-2xl border-4 border-red-300 shadow-xl">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2" style={{
                background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Messages Management</h1>
              <p className="text-gray-700 text-lg font-medium">View and reply to user messages</p>
            </div>
            <Button 
              onClick={() => navigate('/admin/dashboard')}
              variant="outline"
              className="border-2 border-red-400"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="border-3 border-gray-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-lg">Total Messages</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-gray-900">{messages.length}</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-orange-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-lg">Unread</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-orange-600">{unreadCount}</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-blue-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-lg">Read</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-blue-600">{readCount}</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-green-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-lg">Replied</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-green-600">{repliedCount}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <Card className="border-3 border-yellow-300 shadow-lg bg-white/90 backdrop-blur-sm mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <label className="font-semibold">Filter by Status:</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Messages</SelectItem>
                  <SelectItem value="unread">Unread</SelectItem>
                  <SelectItem value="read">Read</SelectItem>
                  <SelectItem value="replied">Replied</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-gray-600">Showing {filteredMessages.length} messages</span>
            </div>
          </CardContent>
        </Card>

        {/* Messages List */}
        <Card className="border-3 border-red-300 shadow-lg bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <CardTitle>All User Messages</CardTitle>
          </CardHeader>
          <CardContent>
            {!filteredMessages || filteredMessages.length === 0 ? (
              <div className="text-center py-12">
                <MessageCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">{filteredMessages ? 'No messages found' : 'Loading messages...'}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredMessages.map((message) => (
                  <Card key={message._id || message.id} className="border-2 hover:border-red-400 transition-colors">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <p className="font-bold text-xl text-gray-900">{message.subject}</p>
                            {getStatusBadge(message.status)}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <User className="h-4 w-4" />
                              {message.userName}
                            </span>
                            <span className="flex items-center gap-1">
                              <Mail className="h-4 w-4" />
                              {message.userEmail}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {new Date(message.createdAt).toLocaleString('en-IN')}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => handleOpenReply(message)}
                          >
                            <Send className="h-4 w-4 mr-2" />
                            Reply
                          </Button>
                          {message.status !== 'Read' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-green-400 text-green-600 hover:bg-green-50"
                              onClick={() => handleCloseMessage(message._id || message.id)}
                            >
                              <CheckCheck className="h-4 w-4 mr-2" />
                              Mark as Read
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Message Content */}
                      <div className="space-y-3 mt-4">
                        {/* User Message */}
                        <div className="p-4 rounded-lg bg-blue-50 border-l-4 border-blue-400">
                          <div className="flex justify-between items-start mb-2">
                            <p className="text-sm font-bold text-gray-700">👤 User</p>
                            <p className="text-xs text-gray-500">
                              {new Date(message.createdAt).toLocaleString('en-IN')}
                            </p>
                          </div>
                          <p className="text-gray-800">{message.message}</p>
                        </div>
                        
                        {/* Admin Reply */}
                        {message.adminReply && (
                          <div className="p-4 rounded-lg bg-green-50 border-l-4 border-green-400">
                            <div className="flex justify-between items-start mb-2">
                              <p className="text-sm font-bold text-gray-700">🛡️ Admin Support</p>
                              <p className="text-xs text-gray-500">
                                {new Date(message.updatedAt).toLocaleString('en-IN')}
                              </p>
                            </div>
                            <p className="text-gray-800">{message.adminReply}</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reply Dialog */}
        <Dialog open={isReplyDialogOpen} onOpenChange={setIsReplyDialogOpen}>
          <DialogContent className="bg-white max-w-2xl">
            <DialogHeader>
              <DialogTitle>Reply to Message</DialogTitle>
              <DialogDescription>
                Send a reply to {selectedMessage?.userName}
              </DialogDescription>
            </DialogHeader>
            {selectedMessage && (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-bold text-gray-900 mb-2">{selectedMessage.subject}</p>
                  <p className="text-sm text-gray-600">From: {selectedMessage.userName} ({selectedMessage.userEmail})</p>
                </div>

                <div className="space-y-2">
                  <label className="font-semibold">Your Reply</label>
                  <Textarea
                    placeholder="Type your reply here..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={6}
                    className="resize-none"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    className="flex-1 text-white border-2 border-blue-400 bg-blue-600 hover:bg-blue-700"
                    onClick={handleSendReply}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Send Reply
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setIsReplyDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MessagesManagement;
